import { ApiClientError, describeApiError } from 'grabbitbot-legacy-ui'

/**
 * Plain-words UI text for the machine error codes api.feesubscription.com
 * emits (same approach as secure.grabbitbot.com's errorMessages.ts, UI lib
 * 0.5.0 `describeApiError`). Each message says what happened AND what to do
 * next. Generic transport/status wording (network failure, 401, 5xx, unknown
 * code) lives in the UI lib; only fee-subscription codes live here.
 *
 * Sources: api.feesubscription.com app/routers/*, app/billing/* (the
 * `{"error": "<code>"}` bodies) and the `LegacyError("<token>", …)` refusals,
 * which arrive as `{"error": "Exception", "message": "<token>"}` — see
 * `tableKey`. Keep in step when the backend adds a code.
 */
export const FEESUBSCRIPTION_ERROR_MESSAGES: Readonly<Record<string, string>> = {
  // --- money: charges, idempotency, unknown outcomes -----------------------
  charge_outcome_unknown:
    'Outcome unknown — do not retry. This payment may or may not have been charged; check Transactions or contact support.',
  attempt_in_progress: 'This payment is still being processed. Wait a minute, then check Transactions before trying again.',
  request_in_progress: 'The same request is still running. Wait a moment, then check the result.',
  invalid_idempotency_key: 'The payment request was not valid. Nothing was charged; reload the page and try again.',
  idempotency_key_reused: 'The payment request was refused (it did not match the earlier attempt). Nothing new was charged.',
  stripe_unavailable: 'The card processor could not be reached. Check Transactions before trying again.',
  stripe_key_mode_mismatch:
    'Card payments are not set up for this system (the project’s Stripe key is for the other mode). Nothing was charged.',
  payment_method_not_set: 'No card is saved yet. Add a payment method first, then try again.',
  missing_payment_method: 'No card is saved yet. Add a payment method first, then try again.',
  paymentmethod_expired: 'The saved card has expired. Add a new payment method, then try again.',
  cant_expire: 'This checkout session can no longer be cancelled.',
  invalid_amount: 'The amount is not valid. Enter a positive amount and try again.',
  amount_mismatch: 'The amount does not match the recorded charge. Nothing was booked; check the attempt again.',
  amount_unknown: 'The charged amount is not known yet, so it cannot be booked. Check the attempt with Stripe first.',
  cannot_book: 'This charge cannot be booked in its current state.',
  invalid_resolution: 'That resolution is not allowed for this charge attempt.',
  charge_attempt_not_found: 'This charge attempt no longer exists.',
  reference_already_used: 'This reference was already booked. Nothing new was booked.',
  // --- subscriptions / catalogue ------------------------------------------
  subscription_not_found: 'There is no subscription yet. Choose a plan and pay first.',
  subscription_not_active: 'The subscription is not active. Renew it first, then try again.',
  subscription_not_due: 'This subscription is not due for renewal yet.',
  plan_not_found: 'That plan was not found.',
  billing_account_not_found: 'No billing account was found for this user in this project.',
  project_not_found: 'This project was not found.',
  cant_send_emails: 'The e-mails could not be sent. Check the project’s e-mail settings and try again.',
  // --- access ---------------------------------------------------------------
  access_denied_to_project: 'You do not have access to this project.',
  access_denied_to_resource: 'You do not have access to this item.',
  // --- Telegram -------------------------------------------------------------
  chat_belongs_to_another_usermanager: 'This chat is already managed by another user manager.',
  chat_belongs_to_another_project: 'This chat already belongs to another project.',
  validation_error: 'Some of the values are not valid. Please check the form and try again.',
}

const TOKEN_RE = /^[a-z][a-z0-9_]*$/

/** The table key of an error: its code, or — for a `LegacyError` whose code is
 * the PHP exception class (`Exception`, `TypeError`) — the machine token the
 * legacy controller put in the message. */
function tableKey(err: ApiClientError): string {
  if (FEESUBSCRIPTION_ERROR_MESSAGES[err.code]) return err.code
  const message = err.message.trim()
  return TOKEN_RE.test(message) && FEESUBSCRIPTION_ERROR_MESSAGES[message] ? message : err.code
}

/** One sentence for any failed call: fee-subscription code table first, then
 * the UI lib's generic wording. `fallback` = what the user was doing, without
 * a full stop ("Could not save the plan"). */
export function describeError(err: unknown, fallback?: string): string {
  // A plain Error is an internal failure, never prose meant for the user
  // (TypeError = fetch failed = network, which the lib names).
  if (!(err instanceof ApiClientError) && !(err instanceof TypeError)) {
    return `${fallback ?? 'The request failed'}.`
  }
  if (err instanceof ApiClientError) {
    const key = tableKey(err)
    const mapped = FEESUBSCRIPTION_ERROR_MESSAGES[key]
    if (mapped) return mapped
    // Legacy soft error whose PROSE is the `error` field itself (HTTP 200
    // `{"error": "Data is NOT from Telegram"}`, src/api/http.ts): the UI lib
    // would treat message === code as a bare token and hide it.
    const message = err.message.trim()
    if (message === err.code && /\s/.test(message)) return message
  }
  return describeApiError(err, { codes: FEESUBSCRIPTION_ERROR_MESSAGES, fallback })
}
